import { useMemo, useCallback, useState } from 'react';
import { Table, Alert, Form, Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircle } from '@fortawesome/free-solid-svg-icons';
import { useAccountUserAccess, useAccount } from 'api/hooks/use-accounts';
import useMunicipalitySettings from 'api/hooks/use-municipality-settings';
import SegmentBadge from './segment-badge';
import TableLoading from 'shared/tables/table-loading';
import TableFooter from 'shared/tables/table-footer';
import {
  SEGMENT_TYPES,
  getSegmentName as getSegmentNameHelper,
  getSegmentValue
} from './helpers';

const SegmentAccessLegend = () => (
  <div className="d-flex align-items-center gap-3 mb-3 text-muted small">
    <span className="fw-semibold">Legend:</span>
    <span className="d-flex align-items-center gap-1">
      <FontAwesomeIcon icon={faCheck} className="text-success" />
      <span>= User has access</span>
    </span>
    <span className="d-flex align-items-center gap-1">
      <FontAwesomeIcon icon={faCircle} size="xs" style={{ color: '#6c757d' }} />
      <span>= No access</span>
    </span>
  </div>
);

const AccountDetailModal = ({ show, onHide, accountId }) => {
  const navigate = useNavigate();
  const perPage = 50;
  const { data: settings, isSuccess: settingsLoaded } = useMunicipalitySettings({ options: {} });

  // Local state for filters and pagination (instead of URL params)
  const [page, setPage] = useState(1);
  const [segmentFilters, setSegmentFilters] = useState({});
  const [hideInactiveRoles, setHideInactiveRoles] = useState(true);

  // Fetch account details
  const { data: accountData } = useAccount({ params: { id: accountId }, options: { enabled: !!accountId } });

  // Build segments query param from active filters
  const activeSegments = useMemo(() => {
    return Object.entries(segmentFilters)
      .filter(([, isActive]) => isActive)
      .map(([accessType]) => accessType);
  }, [segmentFilters]);

  const segmentsParam = activeSegments.length > 0 ? activeSegments.join(',') : undefined;

  const {
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    data
  } = useAccountUserAccess({
    params: { id: accountId, page, perPage, segments: segmentsParam, hideInactiveRoles },
    options: { enabled: !!accountId }
  });

  const getSegmentName = (accessType) => getSegmentNameHelper(settings, settingsLoaded, accessType);

  // Get account from either source
  const account = data?.account || accountData;

  // Get which segments are present in the account
  const availableSegments = useMemo(() => {
    if (!account) return [];
    return SEGMENT_TYPES.filter(segmentType => account[segmentType]?.code);
  }, [account]);

  // Toggle a segment filter and reset page to 1
  const toggleFilter = (accessType) => {
    setSegmentFilters(prev => ({ ...prev, [accessType]: !prev[accessType] }));
    setPage(1);
  };

  // Clear all filters and reset page
  const clearFilters = () => {
    setSegmentFilters({});
    setPage(1);
  };

  // Toggle hide inactive roles filter
  const toggleHideInactiveRoles = () => {
    setHideInactiveRoles(prev => !prev);
    setPage(1);
  };

  // Adapter for TableFooter
  const paginationParams = useMemo(() => ({ page, perPage }), [page, perPage]);
  const setPaginationParams = useCallback((newParams) => {
    const resolved = typeof newParams === 'function' ? newParams(paginationParams) : newParams;
    setPage(resolved.page || 1);
  }, [paginationParams]);

  // Get active filter count
  const activeFilterCount = activeSegments.length;

  const { users, pagination } = data || {};
  const { total_pages, total_count } = pagination || {};

  // Build meta object for TableFooter
  const paginationMeta = {
    pages: total_pages || 1,
    total: total_count || users?.length || 0,
    title: 'Users'
  };

  const handleRowClick = (userId) => {
    navigate(`/users/${userId}?tab=data-access`);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title className="h5">
          {account ? (
            <span>
              Account <span className="font-monospace">{account.full_account}</span> - Employee Access
            </span>
          ) : (
            'Account Details'
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <Table hover responsive size="sm" className="border shadow-sm bg-white">
            <thead>
              <tr>
                <th>Employee</th>
                <th className="text-center">Loading segments...</th>
              </tr>
            </thead>
            <tbody>
              <TableLoading columns={2} />
            </tbody>
          </Table>
        )}

        {isError && (
          <Alert variant="danger">
            <strong>Error loading account access:</strong>{' '}
            {error?.message || 'An unexpected error occurred.'}
          </Alert>
        )}

        {isSuccess && data && (
          <>
            {/* Account Info */}
            <div className="mb-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0 12px' }}>
              <div><strong>Account Number</strong></div>
              <div><strong>Type</strong></div>
              <div><strong>Description</strong></div>
              <div className="font-monospace">{account.full_account}</div>
              <div>{account.account_type_name}</div>
              <div>{account.account_description}</div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <SegmentAccessLegend />
              <Form.Check
                type="switch"
                id="hideInactiveRoles"
                label="Hide Inactive User Roles"
                checked={hideInactiveRoles}
                onChange={toggleHideInactiveRoles}
                className="user-select-none"
              />
            </div>

            {/* Segment Filters */}
            {availableSegments.length > 0 && (
              <div className="mb-3 p-3 bg-light rounded border">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-semibold small text-muted">
                    Filter by Segment Access:
                  </span>
                  {activeFilterCount > 0 && (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none"
                      onClick={clearFilters}
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {availableSegments.map(accessType => {
                    const segmentValue = getSegmentValue(account, accessType);
                    const isActive = segmentFilters[accessType];
                    return (
                      <Form.Check
                        key={accessType}
                        type="checkbox"
                        id={`filter-${accessType}`}
                        label={
                          <span className="small">
                            {getSegmentName(accessType)}: <code>{segmentValue}</code>
                          </span>
                        }
                        checked={isActive || false}
                        onChange={() => toggleFilter(accessType)}
                        className="user-select-none"
                      />
                    );
                  })}
                </div>
                {activeFilterCount > 0 && (
                  <div className="mt-2 small text-muted">
                    Showing users with access to {activeFilterCount === 1 ? 'selected segment' : 'all selected segments'}
                    {isFetching && ' (updating...)'}
                  </div>
                )}
              </div>
            )}

            {isFetching ? (
              <span className="text-muted ms-2">Loading...</span>
            ) : users.length === 0 ? (
              <Alert variant="info" className="text-center py-3">
                {activeFilterCount > 0
                  ? 'No users match the selected filters.'
                  : 'No employees have access to this account.'}
              </Alert>
            ) : (
              <Table hover responsive size="sm" className="border shadow-sm bg-white">
                <thead>
                  <tr>
                    <th>Employee</th>
                    {availableSegments.map(accessType => (
                      <th key={accessType} className="text-center" style={{ whiteSpace: 'nowrap' }}>
                        <div className="small">{getSegmentName(accessType)}</div>
                        <code className="small">{getSegmentValue(account, accessType)}</code>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={`user-${user.employee.id}`}
                      onClick={() => handleRowClick(user.employee.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div className="fw-bold">{user.employee.full_name}</div>
                        <div className="text-muted small">{user.employee.department?.external_id} {user.employee.department?.name}</div>
                      </td>
                      {availableSegments.map(accessType => {
                        const segmentValue = getSegmentValue(account, accessType);
                        const hasAccess = user.segment_access[accessType];
                        return (
                          <td key={accessType} className="text-center">
                            <SegmentBadge
                              segmentName={getSegmentName(accessType)}
                              segmentValue={segmentValue}
                              hasAccess={hasAccess}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {paginationMeta.total > 0 && (
              <TableFooter
                meta={paginationMeta}
                params={paginationParams}
                setParams={setPaginationParams}
                hidePerPage
              />
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AccountDetailModal;
